import { useSortable } from "@dnd-kit/sortable";
import {
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  TagsInput,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { IconGripVertical, IconShare3, IconX } from "@tabler/icons-react";
import { CSS } from "@dnd-kit/utilities";
import { useConfigStore } from "../../stores/ConfigStore";
import { useLanguage } from "../../stores/LanguageStore";
import Selector from "../Selectors/Selector";
import { useMemo, useState } from "react";
import { getFields } from "../../api/ContactAPI";

function getOptionName(option) {
  return `${option.name}`;
}

const GrapeItem = ({ item, setItems, index, items }) => {
  const lang = useLanguage((s) => s.language);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });
  const datafields = useConfigStore((s) => s.datafields);
  const [loading, setLoading] = useState(false);
  const handleLoad = () => {
    setLoading(true);
    getFields().finally(() => {
      setLoading(false);
    });
  }; 

  const options = useMemo(() => {
    let result = [
      {
        label: lang.global.defaults,
        options: [],
      },
      { label: lang.global.created, options: [] },
    ];
    datafields?.forEach((item) => {
      let default_datafield = item?.from_sharing && item?.created_by === null;
      result[default_datafield ? 0 : 1].options.push({
        ...item,
        option_name: getOptionName(item),
        disabled: items.find((x) => x.id === item.id),
      });
    });
    return datafields?.length > 0 ? result : [];
  }, [datafields, items, lang]);

  const handleFieldChange = (val) => {
    setItems((prev) => {
      return prev.map((x, i) => {
        if (i === index) {
          return { ...item, ...val, label: val.name, placeholder: val.name };
        }
        return x;
      });
    });
  };

  const handlePropChange = ({ field, newValue }) => {
    setItems((prev) => {
      return prev.map((x, i) => {
        if (i === index) {
          return { ...item, [field]: newValue };
        }
        return x;
      });
    });
  };

  const handleDelete = () => {
    let newItems = [...items];
    newItems.splice(index, 1);

    setItems(newItems);
  };

  return (
    <Paper
      component={Group}
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: 10,
        border: "1px solid #00000021",
      }}
      p={"xs"}
      radius={"md"}
      gap={"xs"}
      wrap="nowrap"
      shadow="sm"
    >
      <IconGripVertical
        size={18}
        style={{ cursor: "grab", opacity: 0.5 }}
        {...attributes}
        {...listeners}
      />
      <Stack flex={"1 1 auto"} w={0} gap={4}>
        <Group wrap="nowrap">
          <Selector
            options={options}
            placeholder={lang.contact_lists.import.no_import_field}
            loading={loading}
            value={item.id}
            onChange={handleFieldChange}
            onDropdownOpen={handleLoad}
            getOptionName={getOptionName}
            unclearable
            rightOptionSection={(item) => (
              <>
                {item?.id && (
                  <span style={{ fontSize: 10, opacity: 0.35 }}>
                    #{item?.id}
                  </span>
                )}
                {item.from_sharing && item?.created_by && (
                  <Tooltip label={lang.prm.details.from_sharing} withArrow>
                    <IconShare3 size={16} />
                  </Tooltip>
                )}
              </>
            )}
          />
          <Button
            variant="light"
            color="red"
            px={8}
            disabled={items.length === 1}
            onClick={handleDelete}
          >
            <IconX />
          </Button>
        </Group>
        <Group>
          <Select
            label="Type"
            placeholder="Type"
            size="xs"
            w={120}
            onChange={(v) =>
              handlePropChange({
                field: "type",
                newValue: v,
              })
            }
            defaultValue={item?.type || "text"}
            data={[
              {
                value: "text",
                label: lang.forms.types.text,
              },
              {
                value: "number",
                label: lang.forms.types.number,
              },
              {
                value: "email",
                label: lang.forms.types.email,
              },
              {
                value: "select",
                label: lang.forms.types.select,
              },
              {
                value: "checkbox",
                label: lang.forms.types.checkbox,
              },
            ]}
          />
          <TextInput
            label={lang.forms.label_label}
            placeholder={lang.forms.label_placeholder}
            size="xs"
            w={item.type === "checkbox" ? "auto" : 125}
            flex={1}
            defaultValue={item?.label}
            onChange={(e) =>
              handlePropChange({
                field: "label",
                newValue: e.target.value,
              })
            }
          />
          {item.type !== "checkbox" && (
            <TextInput
              label={lang.forms.placeholder_label}
              placeholder={lang.forms.placeholder_label}
              size="xs"
              w={125}
              defaultValue={item?.placeholder}
              onChange={(e) =>
                handlePropChange({
                  field: "placeholder",
                  newValue: e.target.value,
                })
              }
            />
          )}
          {item.type !== "checkbox" && (
            <Checkbox
              label={lang.forms.required}
              ml={"auto"}
              mt={20}
              size="xs"
              onChange={(e) =>
                handlePropChange({
                  field: "required",
                  newValue: e.target.checked,
                })
              }
              defaultChecked={item.required}
            />
          )}
        </Group>

        {item.type === "checkbox" && (
          <Group wrap="nowrap">
            <TextInput
              label={lang.forms.checked_value}
              placeholder={lang.forms.checked_value_placeholder}
              size="xs"
              w={"100%"}
              defaultValue={item?.checked_value}
              onChange={(e) =>
                handlePropChange({
                  field: "checked_value",
                  newValue: e.target.value,
                })
              }
            />
            <TextInput
              label={lang.forms.not_checked_value}
              placeholder={lang.forms.not_checked_value_placeholder}
              size="xs"
              w={"100%"}
              defaultValue={item?.unchecked_value}
              onChange={(e) =>
                handlePropChange({
                  field: "unchecked_value",
                  newValue: e.target.value,
                })
              }
            />
          </Group>
        )}
        {item.type === "select" && (
          <TagsInput
            required
            label={lang.forms.values_list}
            placeholder={lang.forms.press_enter}
            defaultValue={item?.options || []}
            onChange={(newValue) =>
              handlePropChange({
                field: "options",
                newValue: newValue,
              })
            }
          />
        )}
      </Stack>
    </Paper>
  );
};

export default GrapeItem;
